// Primitivas compartidas por las funciones puras de filtrado (#247, D2). Replican en el
// navegador la semántica EXACTA del SQL del servidor, que es lo que exige la regla de
// equivalencia (R7): mismo cotejo de texto, mismo trato de los ids y mismos días naturales
// en UTC. Nada de esto se usa cuando hay red: entonces filtra el servidor.

/**
 * casaTexto replica `columna ILIKE '%q%'` con los comodines escapados: comparación de
 * subcadena SIN distinguir mayúsculas, y `%`/`_` tratados como caracteres literales (en el
 * cliente lo son por construcción, al no haber patrón). Una columna nula se trata como
 * cadena vacía, igual que el COALESCE(col, '') del servidor.
 */
export function casaTexto(
  q: string | undefined,
  ...columnas: (string | null | undefined)[]
): boolean {
  const aguja = (q ?? "").trim().toLocaleLowerCase();
  if (aguja === "") {
    return true;
  }
  return columnas.some((c) => (c ?? "").toLocaleLowerCase().includes(aguja));
}

/** casaID replica `columna = ?` para un id que en la URL viaja como texto. */
export function casaID(filtro: string | undefined, valor: number | null | undefined): boolean {
  if (filtro === undefined || filtro === "") {
    return true;
  }
  return valor != null && String(valor) === filtro;
}

/** casaBool replica `columna = ?` para un booleano que en la URL viaja como "true"/"false". */
export function casaBool(filtro: string | undefined, valor: boolean): boolean {
  if (filtro === undefined || filtro === "") {
    return true;
  }
  return filtro === String(valor);
}

/** casaIgual replica `columna = ?` para un enumerado o un texto exacto. */
export function casaIgual(filtro: string | undefined, valor: string | null | undefined): boolean {
  if (filtro === undefined || filtro === "") {
    return true;
  }
  return valor === filtro;
}

const UN_DIA_MS = 24 * 60 * 60 * 1000;

/**
 * casaDia replica el rango de días naturales UTC del servidor (D4): `desde` abre el día a
 * las 00:00Z y `hasta` es INCLUSIVO (el servidor compara con `< hasta + 1 día`). `fecha` es
 * el instante ISO que publica el API; nula queda fuera de cualquier rango acotado, igual
 * que una columna NULL en SQL.
 */
export function casaDia(
  fecha: string | null | undefined,
  desde: string | undefined,
  hasta: string | undefined,
): boolean {
  const hayDesde = desde !== undefined && desde !== "";
  const hayHasta = hasta !== undefined && hasta !== "";
  if (!hayDesde && !hayHasta) {
    return true;
  }
  if (fecha == null || fecha === "") {
    return false;
  }
  const t = Date.parse(fecha);
  if (Number.isNaN(t)) {
    return false;
  }
  if (hayDesde) {
    const inicio = Date.parse(`${desde}T00:00:00.000Z`);
    // Un límite que no se puede interpretar (`?desde=01-03-2026`) NO deja pasar nada. Con
    // red eso es un 400 del servidor; sin red no hay quien lo rechace, y dejarlo pasar
    // evaporaría el filtro EN SILENCIO mientras el chip sigue contándolo como activo.
    if (Number.isNaN(inicio) || t < inicio) {
      return false;
    }
  }
  if (hayHasta) {
    const finExclusivo = Date.parse(`${hasta}T00:00:00.000Z`);
    // Límite EXCLUSIVO del día siguiente: eso es lo que hace inclusivo a `hasta`.
    if (Number.isNaN(finExclusivo) || t >= finExclusivo + UN_DIA_MS) {
      return false;
    }
  }
  return true;
}
